/**
 * useTokens — resolve the design tokens (src/styles/tokens.css) to concrete color
 * strings for SVG charts, which need real values, not `var(--x)` attributes. Reads
 * getComputedStyle on the root and re-reads when the theme flips. Falls back to the
 * dark palette when the DOM has no computed styles (jsdom tests), so charts render
 * deterministically under test.
 */

import { useEffect, useState } from 'react'
import { useTheme } from '../../../contexts/ThemeContext'

export type Tokens = Record<string, string>

const DEFAULTS: Tokens = {
  bg: '#0a0e1a', bg2: '#0c1120', panel: '#111827', panel2: '#18223a', elev: '#151d30',
  line: '#26314a', line2: '#1b2437', ink: '#e8edf7', mut: '#94a3bd', mut2: '#5d6b85',
  brand: '#4b8bf5', blue: '#4b8bf5', orange: '#f59e0b', green: '#12b981', purple: '#8b5cf6',
  cyan: '#22d3ee', pink: '#f472b6', red: '#ef4444', good: '#10b981', bad: '#ef4444',
  amber: '#f59e0b', neutral: '#64748b',
  'good-bg': 'rgba(16,185,129,.13)', 'bad-bg': 'rgba(239,68,68,.14)',
  'warn-bg': 'rgba(245,158,11,.14)', 'info-bg': 'rgba(75,139,245,.13)',
}

const NAMES = Object.keys(DEFAULTS)

function readTokens(): Tokens {
  const out: Tokens = { ...DEFAULTS }
  if (typeof window === 'undefined' || !window.getComputedStyle) return out
  const cs = window.getComputedStyle(document.documentElement)
  for (const name of NAMES) {
    const value = cs.getPropertyValue(`--${name}`).trim()
    if (value) out[name] = value
  }
  return out
}

export function useTokens(): Tokens {
  const { theme } = useTheme()
  const [tokens, setTokens] = useState<Tokens>(readTokens)
  useEffect(() => {
    setTokens(readTokens())
  }, [theme])
  return tokens
}
